import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ChevronLeft, Clock } from 'lucide-react';

export default function BlogVerifiedReviews() {
  return (
    <div className="bg-[#F4F4F4] min-h-screen text-black font-poppins pb-20">
      <Helmet>
        <title>How to Turn Client Feedback into Verified Online Reviews | Sitemanifest</title>
        <meta name="description" content="Learn how to transform client feedback into trusted, verified online reviews that boost credibility and conversions." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "headline": "How to Turn Client Feedback into Verified Online Reviews",
            "image": ["https://wnjcqclqpgqdyyomobeh.supabase.co/storage/v1/object/public/Blogs/Group%208816.png"],
            "datePublished": "2026-04-04",
            "author": [{
              "@type": "Person",
              "name": "Founder",
              "url": "https://sitemanifest.com"
            }]
          })}
        <link rel="canonical" href="https://sitemanifest.com/blogs/verified-reviews" />
        </script>
      </Helmet>

      <div className="max-w-5xl mx-auto px-6 pt-32">
        {/* Back Link */}
        <Link to="/blogs" className="group inline-flex items-center gap-2 text-zinc-500 hover:text-black transition-colors mb-12 text-[10px] font-black uppercase tracking-widest">
          <ChevronLeft size={14} className="group-hover:-translate-x-1 transition-transform" /> Back to Archives
        </Link>

        {/* Hero Section */}
        <div className="flex flex-col md:flex-row items-center gap-10 mb-16">
          {/* Text Column */}
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-4">
              <div className="bg-black text-white px-3 py-1 text-[10px] font-black uppercase tracking-widest">Guides</div>
              <div className="flex items-center gap-1 text-[10px] font-bold uppercase text-zinc-500 tracking-widest">
                <Clock size={10} /> 5-Minute Read
              </div>
            </div>
            <h1 className="text-5xl md:text-7xl font-black italic leading-[0.85] tracking-tighter mb-6 uppercase break-words">
              How to Turn Client Feedback into Verified Online Reviews
            </h1>
            <p className="text-lg text-zinc-800 font-medium mb-6">
              Transform your customer feedback into authentic, trusted reviews that boost credibility and conversions. Learn the workflow for verified reviews, screenshot conversion, and embedding trust signals on your website.
            </p>
            <Link to="/signup" className="inline-block bg-black text-white px-12 py-4 font-black uppercase tracking-[0.15em] hover:bg-zinc-800 transition-colors">
              Start Free Trial
            </Link>
          </div>

          {/* Image Column */}
          <div className="flex-1">
            <img 
              src="https://wnjcqclqpgqdyyomobeh.supabase.co/storage/v1/object/public/Blogs/Group%208816.png" 
              alt="Verified Reviews Illustration" 
              className="w-full rounded-xl shadow-lg object-cover transition-all duration-1000" 
            />
          </div>
        </div>

        {/* Blog Content */}
        <article className="prose prose-zinc max-w-none 
          prose-h2:text-3xl prose-h2:font-black prose-h2:uppercase prose-h2:italic prose-h2:tracking-tight prose-h2:border-b-4 prose-h2:border-black prose-h2:pb-2
          prose-p:text-zinc-800 prose-p:text-lg prose-p:leading-relaxed prose-p:font-medium
          prose-strong:text-black prose-strong:font-black
          prose-blockquote:border-l-8 prose-blockquote:border-black prose-blockquote:bg-white prose-blockquote:p-6 prose-blockquote:italic
          prose-a:text-blue-700 prose-a:font-bold hover:prose-a:bg-black hover:prose-a:text-white transition-all">

          <h2><span className="inline-block mr-2 px-2 py-1 bg-black text-white font-bold rounded">1</span> Why Verified Reviews Matter</h2>
          <p>
            Businesses today are judged by trustworthiness as much as marketing. Fake or unverified reviews can harm credibility and reduce conversions. Verified reviews provide authenticity, giving potential clients confidence in your services.
          </p>


          <h2><span className="inline-block mr-2 px-2 py-1 bg-black text-white font-bold rounded">2</span> Collecting Client Feedback</h2>
          <p>
            Upload your client lists to Sitemanifest and send invitations via email. Invited users can leave verified reviews without needing to upload proof, while guest reviews remain clearly labeled for transparency.
          </p>

          <h2><span className="inline-block mr-2 px-2 py-1 bg-black text-white font-bold rounded">3</span> Converting Screenshots Into Reviews</h2>
          <p>
            Many clients provide feedback via chat or email. Our platform allows businesses to convert these screenshots into verified reviews. Each conversion is moderated and labeled <strong>“Converted”</strong>, ensuring transparency and trust.
          </p>

          <h2><span className="inline-block mr-2 px-2 py-1 bg-black text-white font-bold rounded">4</span> Embedding Reviews on Your Website</h2>
          <p>
            Showcase your verified reviews using our widgets. Highlighting authentic client feedback prominently improves trust and increases conversion rates.
          </p>

          <blockquote>
            Verified reviews are more than testimonials—they’re trust signals that influence client decisions.
          </blockquote>

          <img src='https://wnjcqclqpgqdyyomobeh.supabase.co/storage/v1/object/public/Blogs/MacBook%20Air%20-%201.png' alt="Sitemanifest widgets" />

          <h2><span className="inline-block mr-2 px-2 py-1 bg-black text-white font-bold rounded">5</span> Best Practices for Global Businesses</h2>
          <p>
            Consistency and transparency are crucial. Request client consent, label converted reviews clearly, and use analytics to measure which testimonials impact conversions most.
          </p>
        </article>

        {/* Refined CTA Block */}
        <div className="mt-24 p-1 rounded-lg border-2 border-black bg-white shadow-[12px_12px_0px_0px_rgba(0,0,0,1)]">
          <div className="p-10 text-center border border-dashed border-black space-y-4">
            <h2 className="text-4xl font-black uppercase italic tracking-tighter">Decision Required</h2>
            <p className="text-zinc-600 font-bold uppercase text-sm tracking-widest">
              Deploy Sitemanifest today or continue risking unverified reviews.
            </p>
            <div className="flex flex-col md:flex-row justify-center gap-6 mt-6">
              <Link to="/signup" className="inline-block bg-black text-white px-12 py-5 font-black uppercase tracking-[0.2em] hover:bg-zinc-800 transition-colors">
                Start Free Trial
              </Link>
              <Link to="/about-us" className="inline-block bg-zinc-200 text-black px-12 py-5 font-black uppercase tracking-[0.2em] hover:bg-zinc-400 transition-colors">
                About Us
              </Link>
            </div>
            <p className="text-zinc-500 italic text-sm mt-4">
              Join thousands of businesses already building trust with verified reviews.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}